import {
  Button as DSButton,
  SingleOptionPropStrict,
  Spinner,
} from '@servicetitan/design-system';
import { Button as AnvilButton } from '@servicetitan/anvil2';
import classNames from 'classnames';
import { FC, useCallback, useMemo, useState } from 'react';
import { JobIdLookupAppearance } from './appearance';
import { JobCard } from './JobCard';
import styles from './JobIdLookup.module.css';
import { JobLookupError } from './JobLookupError';
import { JobNumberNotFound } from './JobNumberNotFound';
import {
  JobNumberSearchField,
  JobNumberSearchFieldLocators,
} from './JobNumberSearchField';
import { JobResult, useJobLookup } from './useJobLookup';

export interface JobIdLookupLocators extends JobNumberSearchFieldLocators {
  /** DOM id applied to the resolved/selected job card. */
  selectedCard?: string;
  /** DOM id applied to the candidate (pre-select) job card. */
  foundCard?: string;
}

export interface JobIdLookupProps {
  /** Tenant the lookup is scoped to. Required so cross-tenant jobs aren't matched. */
  tenantId: string;
  /**
   * Fires when the agent confirms a job from the candidate card, with the
   * standard `{ value, text }` option payload (`resolvedJob` carries the full
   * record for callers that need more than the id). Also fires with `{}` when
   * the agent clicks "Change Job" to clear an earlier selection.
   */
  onChange: (
    job: SingleOptionPropStrict,
    resolvedJob?: JobResult | null,
  ) => void;
  /**
   * Parent-driven validity signal — when `true`, the input's required-field
   * error is shown even if the user hasn't interacted yet. Forwarded to
   * `JobNumberSearchField` as `invalidToSubmit`. The parent can flip this
   * back to `false` to clear its half of the signal.
   */
  isInvalid?: boolean;
  /**
   * When true, treat a found-but-already-linked job as a hard error and refuse
   * to let the agent select it. Used for outcomes (Booked) where linking a
   * second call to the same job would create a duplicate lead-call association.
   */
  checkAlreadyLinked?: boolean;
  /** DOM ids forwarded to the input, search button, and result cards for E2E. */
  locators?: JobIdLookupLocators;
  /**
   * Visual variant. Defaults to `classic` so existing callers (Conversation
   * reclassification) keep their design-system styling; pass `anvil2` from
   * Anvil2-styled forms to swap the input, suffix button and result card to
   * the matching widgets.
   */
  appearance?: JobIdLookupAppearance;
}

/**
 * Resolution state of the most recent search. Drives which of the inline
 * panels (candidate card / not-found / already-linked / error) renders.
 */
enum SearchStatus {
  /** No search has run, or the previous result was cleared by the agent. */
  Idle = 'idle',
  /** A matching tenant job was found and is awaiting confirmation. */
  Found = 'found',
  /** The search ran successfully but returned no matches. */
  NotFound = 'notFound',
  /** Match exists, but a `leadCallId` is already attached (Booked guard). */
  AlreadyLinked = 'alreadyLinked',
  /** The search itself failed (GraphQL / network error). */
  Error = 'error',
}

/** Whether the lookup is in entry mode or showing a confirmed selection. */
enum ViewMode {
  /** Showing the resolved/selected job card with a "Change Job" affordance. */
  Read = 'read',
  /** Showing the search input so the agent can enter / re-enter a job number. */
  Edit = 'edit',
}

/**
 * Canonical job-number lookup used wherever an agent needs to enter a job
 * number and confirm against a real tenant job (Conversation reclassification,
 * Speed-to-Lead "Booked" outcome gate). Wraps `useJobLookup` plus the input,
 * candidate preview, "no results" state, and an "already-linked" guard for
 * Booked-style outcomes.
 */
export const JobIdLookup: FC<JobIdLookupProps> = ({
  tenantId,
  onChange,
  isInvalid,
  checkAlreadyLinked,
  locators,
  appearance = 'classic',
}) => {
  const [searchStatus, setSearchStatus] = useState<SearchStatus>(
    SearchStatus.Idle,
  );
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Edit);
  const [jobNumber, setJobNumber] = useState('');

  const { refetchSearchJob, jobResult, loading } = useJobLookup();

  const onSearchJobNumber = useCallback(async () => {
    if (jobNumber.trim().length > 0 && tenantId) {
      // Apollo v4: useLazyQuery's default errorPolicy 'none' rejects on
      // GraphQL/network errors instead of resolving with `response.error`.
      // Scope the catch to just the await so the synchronous state mapping
      // below still propagates any unexpected errors
      let response: Awaited<ReturnType<typeof refetchSearchJob>>;
      try {
        response = await refetchSearchJob({
          variables: { tenantId, jobNumber },
        });
      } catch {
        // Surface GraphQL/network failures as a distinct error panel —
        // falling through to NotFound would tell the agent to retype a job
        // number they already know is correct.
        setSearchStatus(SearchStatus.Error);
        return;
      }

      setSearchStatus(() => {
        if (!response.data?.jobs?.items?.length) {
          return SearchStatus.NotFound;
        }

        return response.data.jobs.items[0].leadCallId && checkAlreadyLinked
          ? SearchStatus.AlreadyLinked
          : SearchStatus.Found;
      });
    }
  }, [refetchSearchJob, tenantId, jobNumber, checkAlreadyLinked]);

  const onChangeJobNumber = useCallback(
    (value: string) => {
      if (
        [
          SearchStatus.Found,
          SearchStatus.NotFound,
          SearchStatus.AlreadyLinked,
          SearchStatus.Error,
        ].includes(searchStatus)
      ) {
        setSearchStatus(SearchStatus.Idle);
      }

      setJobNumber(value);
    },
    [searchStatus],
  );

  const updateSelectedJob = useCallback(() => {
    setViewMode(ViewMode.Edit);
    setJobNumber('');
    setSearchStatus(SearchStatus.Idle);

    // update parent job state to not allow save changes without job selection
    onChange({}, null);
  }, [onChange]);

  const onSelectJob = useCallback(() => {
    onChange(
      {
        value: jobResult?.id,
        text: `Job # ${jobResult?.id}`,
      },
      jobResult,
    );
    setViewMode(ViewMode.Read);
  }, [jobResult, onChange]);

  const conditions = useMemo(
    () => ({
      isReadMode: viewMode === ViewMode.Read,
      hasFoundJob: !loading && searchStatus === SearchStatus.Found,
      isNotFoundJob: !loading && searchStatus === SearchStatus.NotFound,
      isAlreadyLinked:
        !loading &&
        !!checkAlreadyLinked &&
        searchStatus === SearchStatus.AlreadyLinked,
      isError: !loading && searchStatus === SearchStatus.Error,
    }),
    [viewMode, searchStatus, loading, checkAlreadyLinked],
  );

  const changeJobButton =
    appearance === 'anvil2' ? (
      <AnvilButton
        appearance='secondary'
        size='small'
        className='m-t-1'
        onClick={updateSelectedJob}
      >
        Change Job
      </AnvilButton>
    ) : (
      <DSButton size='small' className='m-t-1' onClick={updateSelectedJob}>
        Change Job
      </DSButton>
    );

  return (
    <div>
      {conditions.isReadMode ? (
        <JobCard
          job={jobResult}
          id={locators?.selectedCard}
          appearance={appearance}
        >
          {changeJobButton}
        </JobCard>
      ) : (
        <>
          <JobNumberSearchField
            value={jobNumber}
            onChange={onChangeJobNumber}
            onSearch={onSearchJobNumber}
            invalidToSubmit={isInvalid}
            isAlreadyLinked={conditions.isAlreadyLinked}
            locators={locators}
            appearance={appearance}
          />

          {loading && (
            <Spinner
              size='small'
              aria-label='loading-spinner'
              className='m-x-auto m-t-2'
            />
          )}

          {conditions.hasFoundJob && (
            <JobCard
              id={locators?.foundCard}
              job={jobResult}
              onClick={onSelectJob}
              elevation='base'
              appearance={appearance}
              className={classNames('m-t-2', styles.jobCard)}
            />
          )}

          {conditions.isNotFoundJob && <JobNumberNotFound />}

          {conditions.isError && <JobLookupError />}
        </>
      )}
    </div>
  );
};
